
import React, { PropsWithChildren, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import SessionStorage from '../../utils/SessionStorage';

import './LimitedEsign.css';
import { cleanInternalLinkUrl } from '../../utils/cob-utils';
import { Button, Row, Col, Modal, Container, ScrollableContainer } from '../../importCL';

interface ILimitedEsignProps extends MappedComponentProperties {
    title?: string,
    description?: string,
    esignTextPath?: string,
    printSectionLogo?: string,

    modalTitle?: string,
    modalDescription?: string,

    endProcessRedirect?: string,
    continueRedirect?: string,
    continueSingleCounterRedirect?: string,
    continueInstantProvisioningRedirect?: string;

    // following props are not passed from AEM and are component only props
    agreeCallback?: () => void;
    declineCallback?: () => void;
}

export const LimitedEsignEditConfig = {
    emptyLabel: 'Limited Esign',
    isEmpty: (props: PropsWithChildren<ILimitedEsignProps>) => {
        return !props || !props.title;
    },
};

const LimitedEsign = (props: PropsWithChildren<ILimitedEsignProps>): JSX.Element => {
    const [isOpen, setOpen] = useState(false);
    const agreeRef = useRef(null);
    const [lockout, setLockout] = useState(false);
    const params = window.location.search;

    const prepareObjectWithSessionValues = async () => {
        const prequalResponse = await SessionStorage.tryGetCipher('prequalResponse', undefined);
        const isSkipPQCounterOffer = prequalResponse?.ApplicationInformation[0] && prequalResponse?.ApplicationInformation[0].IsCounterOffer;
        const sessionValues = {
            programNumber: SessionStorage.get('programNumber') || '',
            isCounterOffer: SessionStorage.get('isCounterOffer') === 'true' || isSkipPQCounterOffer,
            isSecuredCardPromotion: SessionStorage.get('IsSecuredCardPromotion') === 'true',
            skipPreQualEnum: prequalResponse?.SubmitApplicationEnum || 0,
            afterCounterOffer: SessionStorage.get('afterCounterOffer'),
            lastPageVisited: SessionStorage.get('lastVisitedPage'),
        };
        return sessionValues;
    };

    const handleAgreeClick = () => {
        if (lockout) {
            return;
        }
        // application-status 
        if (props.agreeCallback) {
            props.agreeCallback();
            return;
        }
        setLockout(true);
        prepareObjectWithSessionValues().then(SessionStorageValues => {
            const isMobileWalletESign = SessionStorage.tryGetObject('rbPricingRequired', false);
            const isSpecialInstantProvisioningEsign = SessionStorage.tryGetObject('isEsignInstantProvisioning', false);
            let redirectUrl = props?.continueRedirect;

            const isCounterSingleOffer = () => {
                const isPqSingleOffer = !isMobileWalletESign && (parseInt(SessionStorageValues.programNumber) === 1 && props.continueSingleCounterRedirect) && SessionStorageValues.lastPageVisited !== 'esign-origination';
                const isSkipPqSingleOffer = !SessionStorageValues.afterCounterOffer && (parseInt(SessionStorageValues.skipPreQualEnum) === 1 || parseInt(SessionStorageValues.skipPreQualEnum) === 5);

                return isPqSingleOffer || isSkipPqSingleOffer;
            };

            if (SessionStorageValues.isCounterOffer && isCounterSingleOffer()) {
                SessionStorage.set('isCounterMultiOffer', 'false');
                redirectUrl = props.continueSingleCounterRedirect;
            }

            if (isMobileWalletESign && !isSpecialInstantProvisioningEsign) {
                redirectUrl = props.continueInstantProvisioningRedirect;
            }

            SessionStorage.set('visitedEsign', 'true');

            /** Developer's Note
             *    For whatever reason, Safari prevents updating the DOM when a redirect is imminent. 
             *    Redirect moved to timeout as a workaround to prevent optimisation preventing disabling of the agree button
             */
            setTimeout(() => {
                window.location.href = cleanInternalLinkUrl(redirectUrl + '.html' + params);
            }, 500);
        }).catch(() => {
            setLockout(false);
        });
    };

    const handleEndProcessClick = () => {
        if (lockout) {
            return;
        }
        // application-status
        if (props.declineCallback) {
            props.declineCallback();
            return;
        }
        setLockout(true);
        window.location.assign(cleanInternalLinkUrl(props.endProcessRedirect + '.html' + params));
    };

    return (
        <Container id='limited-esign' className='LimitedEsign'>
            <Row>
                <Col>
                    {props.title && <h2 className='LimitedEsign__title no-print'>{transformHTMLParserLinks(props.title)}</h2>}
                    {props.description && <p className='LimitedEsign__description no-print'>{transformHTMLParserLinks(props.description)}</p>}
                </Col>
            </Row>
            {props.esignTextPath &&
                <ScrollableContainer className='LimitedEsign__esignContent py-0' addBorder>
                    <iframe aria-label="Limited Electronic Disclosure and Communications Consent Agreement content" src={`${props.esignTextPath}.html`} className='w-full h-full' />
                </ScrollableContainer>
            }
            <Row justify='end' className='no-print'>
                <div className='LimitedEsign__agreeRow'>
                    <Button className='LimitedEsign__disagreeButton'
                        onClick={() => {
                            if (!lockout) {
                                setOpen((current) => !current)
                            }
                        }}
                        size='small'
                        variant='ghosted-dark'
                    >
                        I Do Not Agree
                    </Button>
                    <Button disableOnClick
                        ref={agreeRef}
                        className='LimitedEsign__agreeButton'
                        size='medium'
                        onClick={handleAgreeClick}
                    >
                        I Agree
                    </Button>
                </div>
            </Row>
            <Modal isOpen={isOpen} modalOpen={setOpen} className='LimitedEsign__modal'>
                {props.modalTitle &&
                    <div className='LimitedEsign__modalTitle'>{props.modalTitle}</div>
                }
                <div className='LimitedEsign__modalContent'>
                    {props.modalDescription &&
                        <p className='LimitedEsign__modalDescription'>
                            {transformHTMLParserLinks(props.modalDescription)}
                        </p>
                    }
                    <Row justify='end'>
                        <div>
                            <Button 
                                className='LimitedEsign__modalButtonBack'
                                size='medium'
                                onClick={() => setOpen((current) => !current)}
                            >
                                Back
                            </Button>
                            <Button 
                                className='LimitedEsign__modalButtonEnd'
                                size='small'
                                variant='ghosted-dark'
                                onClick={handleEndProcessClick}
                            >End Process</Button>
                        </div>
                    </Row>
                </div>
            </Modal>
        </Container>);
};

export default LimitedEsign;
